import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatSize(bytes) {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/** Reusable Transaction Documents section — used by both broker AgentTransaction view and Agent Portal.
 * Handles PDF upload (≤10MB), listing, download (blob), and soft-delete.
 * theme="light" (broker card) | "dark" (agent portal card). */
export default function TransactionDocuments({ txnId, theme = "light", currentActorId = "" }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/transactions/${txnId}/documents`);
      setDocs(res.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load documents");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (txnId) load(); /* eslint-disable-next-line */ }, [txnId]);

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    setErr("");
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setErr("Only PDF files are allowed.");
      e.target.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErr("File too large. max 10 MB.");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/transactions/${txnId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Upload failed. Try again.");
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  };

  const download = async (doc) => {
    try {
      const res = await axios.get(`${API}/transactions/${txnId}/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.originalFilename || "document.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Download failed");
    }
  };

  const del = async (doc) => {
    if (!window.confirm(`Delete "${doc.originalFilename}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/transactions/${txnId}/documents/${doc.id}`);
      setDocs((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Delete failed");
    }
  };

  const dark = theme === "dark";
  const wrap = dark
    ? "rounded-3xl border border-[#C4A56B]/30 bg-[#141414] p-5 text-[#E0D2A8]"
    : "rounded-3xl border border-slate-200 bg-white p-5 text-slate-800";
  const label = dark ? "text-[#C4A56B]" : "text-[#1E1E1E]";
  const rowBg = dark ? "bg-[#0F0F0F]" : "bg-slate-50";
  const btnPrimary = dark
    ? "rounded-full bg-[#C4A56B] px-4 py-2 text-xs font-bold text-[#1E1E1E] hover:bg-[#d4b876] disabled:opacity-60"
    : "rounded-full bg-[#1E1E1E] px-4 py-2 text-xs font-bold text-white hover:bg-black disabled:opacity-60";
  const btnGhost = dark
    ? "rounded-full border border-[#C4A56B]/40 px-3 py-1 text-[11px] font-bold text-[#C4A56B] hover:bg-[#C4A56B]/10"
    : "rounded-full border border-slate-300 px-3 py-1 text-[11px] font-bold text-slate-700 hover:bg-slate-100";
  const btnDanger = dark
    ? "rounded-full border border-red-400/40 px-3 py-1 text-[11px] font-bold text-red-300 hover:bg-red-950/40"
    : "rounded-full border border-red-300 px-3 py-1 text-[11px] font-bold text-red-700 hover:bg-red-50";

  return (
    <section className={wrap} data-testid="transaction-documents">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className={`text-[10px] font-bold uppercase tracking-[0.18em] ${label}`}>Transaction Documents</div>
          <div className={`mt-1 text-sm ${dark ? "text-[#E0D2A8]/70" : "text-slate-600"}`}>
            {docs.length} PDF{docs.length === 1 ? "" : "s"} on file. Max 10 MB per upload.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept="application/pdf,.pdf" onChange={onFile} className="hidden" data-testid="txn-doc-file-input" />
          <button data-testid="txn-doc-upload-btn" disabled={uploading} onClick={onPick} className={btnPrimary}>
            {uploading ? "Uploading…" : "+ Upload PDF"}
          </button>
        </div>
      </div>

      {err && <div className="mt-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="txn-doc-error">{err}</div>}

      {loading ? (
        <div className={`mt-4 text-xs ${dark ? "text-[#E0D2A8]/50" : "text-slate-500"}`}>Loading…</div>
      ) : docs.length === 0 ? (
        <div className={`mt-4 rounded-2xl border border-dashed ${dark ? "border-[#C4A56B]/30 text-[#E0D2A8]/60" : "border-slate-300 text-slate-500"} p-6 text-center text-sm`}>
          No documents uploaded yet. Contracts, inspections, appraisals belong here.
        </div>
      ) : (
        <ul className="mt-4 space-y-2">
          {docs.map((d) => {
            const canDelete = currentActorId ? d.uploadedById === currentActorId || d.uploadedByRole === "admin_ignore_check" : true;
            return (
              <li key={d.id} data-testid={`txn-doc-row-${d.id}`} className={`flex flex-wrap items-center gap-3 rounded-xl ${rowBg} px-4 py-3`}>
                <div className="flex-1 min-w-[200px]">
                  <div className={`text-sm font-bold ${dark ? "" : "text-slate-800"}`}>📄 {d.originalFilename}</div>
                  <div className={`mt-0.5 text-[11px] ${dark ? "text-[#E0D2A8]/60" : "text-slate-500"}`}>
                    {formatSize(d.size)} · uploaded by {d.uploadedByName || d.uploadedByEmail || d.uploadedByRole} · {d.createdAt ? new Date(d.createdAt).toLocaleString() : ""}
                  </div>
                </div>
                <button data-testid={`txn-doc-download-${d.id}`} onClick={() => download(d)} className={btnGhost}>Download</button>
                {canDelete && <button data-testid={`txn-doc-delete-${d.id}`} onClick={() => del(d)} className={btnDanger}>Delete</button>}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
